import { Button, Loader, Text, TextInput } from '@southfields-digital/mpxlive-components';
import { useEffect, useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getRepeaterItems } from 'src/components/GraphicPropertiesForm';
import Repeater from 'src/components/Repeater';
import { StateType } from 'src/redux/reducers';
import { REQUEST_GET_SPORT_DATA_ASKIDALINEUP } from 'src/redux/reducers/sportData';

import { getPropertyValues } from '../utils';

import styles from './AskiDaLineup.module.scss';
import { AskiDaLineupResponse, AskiDaPlayer } from 'src/types/askida/lineup';
import { Value } from 'src/components/Select/types';

type Props = {
  handleImportData: (values: PropertyValue[]) => void;
  selectedGraphic: PlaylistGraphic;
  selectedProperty: RepeaterProperties;
};

const filterPlayers = (lineup: AskiDaLineupResponse[] | AskiDaLineupResponse): AskiDaPlayer[] => {
  if (!lineup) return [];

  // Handle both array and single object responses
  const lineupData = Array.isArray(lineup) ? lineup[0] : lineup;
  if (!lineupData) return [];

  const players: AskiDaPlayer[] = [];
  
  // Iterate through players
  for (let i = 1; i <= 23; i++) {
    const nameKey = `P${i}_Name` as keyof AskiDaLineupResponse;
    const idKey = `P${i}_id` as keyof AskiDaLineupResponse;
    
    const playerName = lineupData[nameKey];
    const playerId = lineupData[idKey];
    
    if (playerName && playerId !== undefined) {
      players.push({
        playerId: playerId.toString(),
        firstName: String(playerName)
      });
    }
  }

  return players;
};

export default function AskiDaLineup({
  handleImportData,
  selectedGraphic,
  selectedProperty,
}: Props) {  
  const dispatch = useDispatch();
  const { AskiDaLineup } = useSelector((state: StateType) => state.sportData);

  const [fixtureId, setFixtureId] = useState<Value>(0);
  const [contestantId, setContestantId] = useState<Value>(0);

  const propertyValues = useMemo(() => {
    const lineup = AskiDaLineup.data ?? [];
    const filteredPlayers = filterPlayers(lineup);
    return getPropertyValues(selectedGraphic, filteredPlayers, []);

  }, [AskiDaLineup?.data || {}, selectedGraphic]);

  const repeaterValues = useMemo(
    () => getRepeaterItems(selectedProperty, propertyValues),
    [propertyValues, selectedProperty]
  );

  const isLoading = AskiDaLineup.loading;
  const hasError = !!AskiDaLineup.error;
  const hasData = repeaterValues.length > 0;

  useEffect(() => {
    if (!fixtureId || !contestantId) return;
    dispatch({ type: REQUEST_GET_SPORT_DATA_ASKIDALINEUP, payload: { matchID: fixtureId, teamID: contestantId } });
  }, [dispatch, fixtureId, contestantId]);

  const handleImportClick = useCallback(
    () => handleImportData(propertyValues),
    [handleImportData, propertyValues]
  );

  return (
    <div className={styles.Container}>
      <TextInput
        value={fixtureId}
        type='number'
        placeholder="Enter Match ID"
        onChange={(e) => setFixtureId(e.currentTarget.value)}
      />
      <div className="py-1"></div>
      <TextInput
        value={contestantId}
        type='number'
        placeholder="Enter Contestant ID"
        onChange={(e) => setContestantId(e.currentTarget.value)}
      />
      {!hasData && !isLoading && !hasError && (
        <Text color="surface" className="mt-2">
          No data available
        </Text>
      )}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader className={styles.Spinner} />
        </div>
      )}
      {hasData && !isLoading && !hasError && (
        <div className={styles.Content}>
          <div className={styles.Header}>
            <Button className={styles.ImportButton} onClick={handleImportClick}>
              Import
            </Button>
          </div>
          <Repeater
            id="lineupRepeater"
            onChangeRows={() => {}}
            items={repeaterValues}
            isDisabled
            excludedIds={['divider', 'highlight']}
          />
        </div>
      )}
    </div>
  );
}
